"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/Button";
import { SYSTEM_ASSETS } from "@/lib/assets";

export default function LandingPage() {
  const router = useRouter();
  const [showSignup, setShowSignup] = useState(false);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/api/billing/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, companyName: company }),
      });
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => router.push("/auth/login"), 3000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-cyan-500/30 selection:text-cyan-900 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200 px-6 lg:px-20 py-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img 
            src={SYSTEM_ASSETS.logoUrl} 
            alt="NautilusOne Logo" 
            className="h-10 md:h-12 object-contain object-left scale-110 md:scale-[1.3] origin-left"
          />
        </div>
        <div className="flex items-center gap-8">
          <Link href="/auth/identity">
            <Button variant="primary" size="md" 
              className="!bg-[#1E3A8A] !text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:!bg-[#1E40AF] transition-all active:scale-95"
            >
              Launch NautilusOne
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 px-6 lg:px-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-full pointer-events-none opacity-40">
           <div className="absolute top-1/4 right-0 w-1/2 h-1/2 bg-cyan-500/10 blur-[150px] rounded-full"></div>
           <div className="absolute bottom-1/4 left-0 w-1/3 h-1/3 bg-blue-500/10 blur-[150px] rounded-full"></div>
        </div>
        
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div className="space-y-10 z-10">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-blue-900 font-bold text-[10px] uppercase tracking-[0.2em] animate-in fade-in slide-in-from-bottom-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              Operational Protocol 2.0
            </div>
            <h1 className="text-6xl lg:text-8xl font-black tracking-tighter text-blue-900 leading-[0.9]">
              An Ultimate <br/>
              <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 italic pr-8">Command</span> <br/>
              <span className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 italic pr-8">Centre</span> <br/>
              For Your Maritime Ops.
            </h1>
            <p className="text-xl text-slate-600 font-medium max-w-lg leading-relaxed">
              Consolidate your entire consulting operations into a single, high-fidelity dashboard. Real-time tracking, compliance automation, and financial oversight.
            </p>
            <div className="flex gap-4 pt-4">
              <Link href="/auth/identity">
                <Button variant="primary" size="md" 
                  className="group relative px-10 py-6 !bg-[#1E3A8A] !text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:!bg-[#1E40AF] transition-all active:scale-95 flex items-center gap-4 overflow-hidden"
                >
                  <span>Launch NautilusOne</span>
                  <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">rocket_launch</span>
                </Button>
              </Link>
            </div>
          </div>
          
          <div className="relative z-10 group">
            <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-[3rem] blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
            <div className="relative bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="/images/marine_command_center_hero.png" 
                alt="Command Center"
                className="w-full h-auto opacity-90 group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-32 px-6 lg:px-20 bg-white border-y border-slate-200 relative">
        <div className="max-w-6xl mx-auto space-y-32">
          <div className="text-center space-y-6">
            <h2 className="text-[10px] font-black uppercase tracking-[0.5em] text-cyan-400">Tactical Advantage</h2>
            <h3 className="text-4xl lg:text-5xl font-black text-blue-900 tracking-tighter">Why Marine Leaders Choose NautilusOne</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Blind Operations",
                desc: "Consultants deployed without real-time coordinate tracking or signal locks.",
                solution: "High-Fidelity Tracking",
                solDesc: "Real-time satellite GPS feed of every agent on duty with tactical map interface.",
                img: "/images/saas_feature_gps_tracking.png"
              },
              {
                title: "Compliance Risk",
                desc: "Expired passports and certifications causing legal and operational delays.",
                solution: "Predictive Security",
                solDesc: "Automated alert protocol for passport, SIC, and qualification expiry 90 days out.",
                img: "/images/saas_feature_compliance_security.png"
              },
              {
                title: "Financial Leakage",
                desc: "Manual expense slips and spreadsheets lead to lost data and payout errors.",
                solution: "Unified Ledger",
                solDesc: "Live digital expense submission and automated payroll calculation engine.",
                img: "/images/saas_feature_financial_ledger.jpg"
              },
              {
                title: "High Initial Investment",
                desc: "Enterprise maritime software often forces companies into expensive, rigid contracts before proving any value.",
                solution: "Free Core Platform",
                solDesc: "Sign up for free and use core modules immediately. Upgrade to advanced modules only when you need to scale productivity.",
                img: "/images/saas_feature_free_tier.jpg"
              }
            ].map((feature, i) => (
              <div key={i} className="group p-10 bg-white border border-cyan-500/30 rounded-3xl transition-all shadow-lg shadow-cyan-500/5">
                {feature.img ? (
                  <img src={feature.img} alt={feature.title} className="w-20 h-20 mb-8 rounded-2xl shadow-md shadow-cyan-500/20" />
                ) : (
                  <div className="h-20 w-20 bg-blue-50 rounded-2xl flex items-center justify-center mb-8 text-blue-900">
                    <span className="material-symbols-outlined text-4xl">{feature.icon}</span>
                  </div>
                )}
                <div className="space-y-4">
                  <div className="space-y-1">
                    <span className="text-red-500 font-bold text-[10px] uppercase tracking-widest">Problem</span>
                    <h4 className="text-xl font-black text-slate-900 tracking-tight">{feature.title}</h4>
                    <p className="text-sm text-slate-600 font-medium">{feature.desc}</p>
                  </div>
                  <div className="pt-6 border-t border-white/5">
                    <span className="text-cyan-400 font-bold text-[10px] uppercase tracking-widest">NautilusOne Solution</span>
                    <h5 className="text-lg font-black text-slate-900 tracking-tight">{feature.solution}</h5>
                    <p className="text-sm text-slate-600 font-medium">{feature.solDesc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing/Signup Modal */}
      {showSignup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowSignup(false)}></div>
          <div className="relative bg-white w-full max-w-lg rounded-3xl p-12 border border-slate-200 shadow-2xl animate-in zoom-in-95 duration-300">
            {success ? (
              <div className="text-center py-20 space-y-6">
                <div className="h-24 w-24 bg-cyan-500 rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-cyan-500/50">
                  <span className="material-symbols-outlined text-white text-5xl">check_circle</span>
                </div>
                <h3 className="text-3xl font-black text-slate-900 tracking-tight">Onboarding Successful</h3>
                <p className="text-slate-600 text-sm font-medium">Your company profile is active. Redirecting to secure login...</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-10 space-y-2">
                  <h3 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">Initialize Hub</h3>
                  <p className="text-slate-600 text-xs font-bold uppercase tracking-widest leading-relaxed">
                    Set up your enterprise command center in under 60 seconds.
                  </p>
                </div>
                
                <form onSubmit={handleSignup} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 ml-2">Admin Identity Email</label>
                    <input 
                      required
                      type="email"
                      placeholder="YOU@COMPANY.COM"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-16 bg-slate-50 border border-slate-200 rounded-2xl px-6 text-slate-900 font-bold text-sm focus:border-cyan-500 outline-none transition-all uppercase tracking-widest"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 ml-2">Company Name</label>
                    <input 
                      required
                      type="text"
                      placeholder="ENTERPRISE NAME"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="w-full h-16 bg-slate-50 border border-slate-200 rounded-2xl px-6 text-slate-900 font-bold text-sm focus:border-cyan-500 outline-none transition-all uppercase tracking-widest"
                    />
                  </div>
                  
                  <div className="bg-cyan-500/10 border border-cyan-500/20 p-6 rounded-2xl space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">Professional License</span>
                      <span className="text-2xl font-black text-slate-900">$499<span className="text-xs text-slate-600 font-medium">/mo</span></span>
                    </div>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-[9px] font-bold text-slate-600 uppercase">
                        <span className="material-symbols-outlined text-[14px] text-cyan-400">check_circle</span>
                        Unlimited Whitelisted Staff
                      </li>
                      <li className="flex items-center gap-2 text-[9px] font-bold text-slate-600 uppercase">
                        <span className="material-symbols-outlined text-[14px] text-cyan-400">check_circle</span>
                        Tactical GPS Surveillance
                      </li>
                    </ul>
                  </div>

                  <Button variant="primary" size="md" 
                    disabled={loading}
                    className="w-full h-16"
                  >
                    {loading ? "Processing Encryption..." : "Confirm & Activate"}
                  </Button>
                  <p className="text-center text-[9px] text-slate-600 font-bold uppercase tracking-widest">
                    Secure checkout via Stripe Protocol
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-20 px-6 lg:px-20 border-t border-white/5 text-center flex flex-col items-center gap-4">
        <div className="text-[10px] text-slate-500 flex flex-wrap justify-center gap-2 items-center">
          <span>Copyright &copy; 2026 NautilusOne. All rights reserved.</span>
          <Link href="/privacy-policy" className="hover:text-blue-500 hover:underline transition-colors">Privacy Policy</Link>
          <span className="text-slate-700">|</span>
          <Link href="/terms-of-use" className="hover:text-blue-500 hover:underline transition-colors">Terms of Use</Link>
          <span className="text-slate-700">|</span>
          <Link href="/sales-and-refunds" className="hover:text-blue-500 hover:underline transition-colors">Sales and Refunds</Link>
          <span className="text-slate-700">|</span>
          <Link href="/legal" className="hover:text-blue-500 hover:underline transition-colors">Legal</Link>
        </div>
        <p className="text-[10px] font-black uppercase tracking-[0.5em] text-slate-600">
          DEVELOPED BY NAUTILUSONE FOR THE MARITIME INDUSTRY
        </p>
      </footer>
    </div>
  );
}
